import {Component, DestroyRef, inject, Input, OnDestroy, OnInit} from '@angular/core';
import {
  AangeverBurgerlijkeStaat,
  AangeverSamenwoonSituatie,
  BokkzIbDataService,
  IncomeTaxEntity,
  Kind
} from "@snelstart-ventures/fiscaal/domain-logic";
import {AbstractControl, FormArray, FormControl, FormGroup, ValidationErrors} from '@angular/forms';
import {SelectFormControlItem, SharedUiFormControlsModule} from '@snelstart/ui-form-controls';
import {debounceTime, distinctUntilChanged, filter, Observable, shareReplay, Subscription, take} from 'rxjs';
import {SharedUiButtonModule} from '@snelstart/ui-button';
import {SharedUiAlertModule} from '@snelstart/ui-alert';
import {SharedUiDetailsModule} from '@snelstart/ui-details';
import {CommonModule} from '@angular/common';
import {SharedUiDialogModule} from '@snelstart/ui-dialog';
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";

@Component({
  selector: 'snelstart-venture-fiscaal-feature-kind',
  templateUrl: './kind.component.html',
  styleUrls: ['./kind.component.scss'],
  imports: [
    SharedUiFormControlsModule,
    SharedUiButtonModule,
    SharedUiAlertModule,
    SharedUiDetailsModule,
    CommonModule,
    SharedUiDialogModule
  ]
})
export class KindComponent implements OnInit, OnDestroy {

  @Input() public id!: string;
  private dataService = inject(BokkzIbDataService);
  private destroyRef = inject(DestroyRef);
  private changeSubscriptions = new Subscription();
  public dialogVisible = false;
  public index = 0;

  readonly incomeTaxEntity$: Observable<IncomeTaxEntity> =
    this.dataService.incomeTaxEntity$.pipe(
      filter((data): data is IncomeTaxEntity => !!data?.sstRelationId),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  public kind = new FormGroup({
    kinderen: new FormArray<
      FormGroup<{
        voornaam: FormControl<string | null>;
        geboortedatum: FormControl<Date | null>;
        datumOverlijden: FormControl<Date | null>;
        bsn: FormControl<string | null>;
        ingeschrevenOpAdresAangever: FormControl<boolean | null>;
        ingeschrevenOpAdresPartner: FormControl<boolean | null>;
        bijExWoonAdres: FormControl<boolean | null>;
        coOuderschap: FormControl<boolean | null>;
        tenMinsteDrieDagenPerWeekWoonachtig: FormControl<boolean | null>;
        ouderOntvangtKinderbijslag: FormControl<boolean | null>;
        onderhoudBelangrijkeMate: FormControl<boolean | null>;
      }>
    >([]),
  });

  public itemsJaNee: SelectFormControlItem<boolean | undefined>[] = [
    {value: true, label: 'Ja'},
    {value: false, label: 'Nee'},
  ];

  showDialog(index: number) {
    this.index = index;
    this.dialogVisible = true;
  }

  public ngOnInit(): void {
    this.incomeTaxEntity$.pipe(
      take(1),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe((data) => {
      this.updateForm(data.aangifte?.kinderen || []);
    })
    this.subscribeToChanges();
  }

  ngOnDestroy(): void {
    // rely on saveFrom in component
  }

  private subscribeToChanges() {
    this.changeSubscriptions.unsubscribe();
    this.changeSubscriptions = new Subscription();

    this.kinderen.controls.forEach(group => {
      this.changeSubscriptions.add(
        group.controls.ingeschrevenOpAdresAangever.valueChanges.pipe(
          debounceTime(500),
          distinctUntilChanged(),
          takeUntilDestroyed(this.destroyRef)
        ).subscribe(() => {
          this.saveForm();
          this.dataService.calculateLatestIncomeTaxReturn().subscribe();
        })
      );
    });
  }

  get kinderen(): FormArray<FormGroup<{
    voornaam: FormControl<string | null>;
    geboortedatum: FormControl<Date | null>;
    datumOverlijden: FormControl<Date | null>;
    bsn: FormControl<string | null>;
    ingeschrevenOpAdresAangever: FormControl<boolean | null>;
    ingeschrevenOpAdresPartner: FormControl<boolean | null>;
    bijExWoonAdres: FormControl<boolean | null>;
    coOuderschap: FormControl<boolean | null>;
    tenMinsteDrieDagenPerWeekWoonachtig: FormControl<boolean | null>;
    ouderOntvangtKinderbijslag: FormControl<boolean | null>;
    onderhoudBelangrijkeMate: FormControl<boolean | null>;
  }>> {
    return this.kind.controls.kinderen;
  }

  addRij(): void {
    this.kinderen.push(this.createKindFormGroup());
    this.subscribeToChanges();
  }

  verwijderRij(index: number): void {
    this.kinderen.removeAt(index, { emitEvent: false });
    this.kind.updateValueAndValidity({emitEvent : false})
    this.saveForm();
    this.subscribeToChanges();
  }

  isGeboortedatumVeldVerplicht(index: number): boolean {
    const group = this.kinderen.at(index);
    const voornaamIngevuld = !!group.get('voornaam')?.value;
    return voornaamIngevuld && (group.get('geboortedatum')?.touched ?? false);
  }

  createKindFormGroup(item?: Kind): FormGroup {
    return new FormGroup({
      voornaam: new FormControl<string | null>(item?.voornaam ?? null),
      geboortedatum: new FormControl<Date | null>(item?.geboortedatum ?? null),
      datumOverlijden: new FormControl<Date | null>(item?.datumOverlijden ?? null),
      bsn: new FormControl<string | null>(item?.bsn ?? null),
      ingeschrevenOpAdresAangever: new FormControl<boolean | null>(item?.ingeschrevenOpAdresAangever ?? false),
      ingeschrevenOpAdresPartner: new FormControl<boolean | null>(item?.ingeschrevenOpAdresAangever ?? false),
      bijExWoonAdres: new FormControl<boolean | null>(item?.ingeschrevenOpAdresAangever ?? false),
      coOuderschap: new FormControl<boolean | null>(item?.ingeschrevenOpAdresAangever ?? false),
      tenMinsteDrieDagenPerWeekWoonachtig: new FormControl<boolean | null>(item?.ingeschrevenOpAdresAangever ?? false),
      ouderOntvangtKinderbijslag: new FormControl<boolean | null>(item?.ingeschrevenOpAdresAangever ?? false),
      onderhoudBelangrijkeMate: new FormControl<boolean | null>(item?.ingeschrevenOpAdresAangever ?? false),
    }, {validators: (group: AbstractControl): ValidationErrors | null => {
      const voornaam = group.get('voornaam')?.value;
      const geboortedatum = group.get('geboortedatum')?.value;
      const bsn = group.get('bsn')?.value;
      const errors: ValidationErrors = {};
      if (voornaam && !geboortedatum) errors['geboortedatumRequired'] = true;
      if (geboortedatum && !bsn) errors['bsnRequired'] = true;
      return Object.keys(errors).length > 0 ? errors : null;
    }});
  }

  private updateForm(kinderen: Kind[]): void {
    const lijst = kinderen || [];
    const formArray = this.kinderen;

    while (formArray.length > lijst.length) {
      formArray.removeAt(formArray.length - 1, {emitEvent: false});
    }

    // Add or update controls
    for (let i = 0; i < lijst.length; i++) {
      if (i < formArray.length) {
        // Update existing control
        const control = formArray.at(i) as FormGroup;
        control.patchValue(lijst[i], {emitEvent: false});
      } else {
        // Add new control
        const formGroup = this.createKindFormGroup(lijst[i]);
        formArray.push(formGroup, {emitEvent: false});
      }
    }
  }

  saveForm(): void {
    this.kind.updateValueAndValidity({ emitEvent: false });

    const kinderen = this.kind.getRawValue().kinderen.reduce<Partial<Kind>[]>((acc, item) => {
      if (!item.voornaam && !item.geboortedatum && !item.bsn) {
        return acc;
      }

      acc.push({
        voornaam: item.voornaam ?? undefined,
        geboortedatum: item.geboortedatum ?? undefined,
        datumOverlijden: item.datumOverlijden ?? undefined,
        bsn: item.bsn ?? undefined,
        ingeschrevenOpAdresAangever: item.ingeschrevenOpAdresAangever ?? false,
        ingeschrevenOpAdresPartner: item.ingeschrevenOpAdresPartner ?? false,
        bijExWoonAdres: item.bijExWoonAdres ?? false,
        coOuderschap: item.coOuderschap ?? false,
        tenMinsteDrieDagenPerWeekWoonachtig: item.tenMinsteDrieDagenPerWeekWoonachtig ?? false,
        ouderOntvangtKinderbijslag: item.ouderOntvangtKinderbijslag ?? false,
        onderhoudBelangrijkeMate: item.onderhoudBelangrijkeMate ?? false,
      });

      return acc;
    }, []) as Kind[];

    this.dataService.patchIncomeTaxEntity(currentEntity => {
      if (!currentEntity.aangifte) {
        return currentEntity;
      }

      return {
        ...currentEntity,
        aangifte: {
          ...currentEntity.aangifte,
          kinderen: kinderen,
        },
      } as IncomeTaxEntity;
    });
  }

  protected readonly AangeverBurgerlijkeStaat = AangeverBurgerlijkeStaat;
  protected readonly AangeverSamenwoonSituatie = AangeverSamenwoonSituatie;
}
